/**
 * Hook đọc permissions từ Redux store.
 * RBAC simple: per-function 3-level Edit / View / NoGrant.
 */
import { useAppSelector } from '@/store'
import type { PermissionLevel } from '@/types/api'

export type PermissionKind = 'view' | 'edit'

const RANK: Record<PermissionLevel, number> = {
  NoGrant: 0,
  View: 1,
  Edit: 2,
}

export function usePermission(functionKey: string | undefined) {
  const permissions = useAppSelector((s) => s.auth.permissions)
  const hasLoadedPerms = Object.keys(permissions).length > 0

  const resolveLevel = (): PermissionLevel => {
    if (!functionKey) return 'Edit'
    // Chưa load permissions → fail-open (hiện tất cả)
    if (!hasLoadedPerms) return 'Edit'
    // Lookup trực tiếp
    const direct = permissions[functionKey]
    if (direct !== undefined) return direct
    // Fallback: 'purchase.npe' → thử key cha 'purchase'
    if (functionKey.includes('.')) {
      const parent = permissions[functionKey.split('.')[0]]
      if (parent !== undefined) return parent
    }
    return 'NoGrant'
  }

  const level = resolveLevel()

  const has = (kind: PermissionKind): boolean => {
    if (!functionKey) return true
    const required = kind === 'edit' ? RANK.Edit : RANK.View
    return RANK[level] >= required
  }

  return {
    level,
    canView: has('view'),
    canEdit: has('edit'),
    has,
  }
}
